import { filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ConferenceActivateAction } from '../../conference/store/conference.action';
import { Action } from '@ngrx/store';
import { Observable } from 'rxjs';
import { Conference } from '../../conference/store/conference.entity';
import {
    WAITINGROOM_IS_ONLINE,
    WAITINGROOM_START_VIDEO_CONFERENCE,
    WAITINGROOM_SWITCH_CONFERENCE,
    WaitingroomAction,
} from './waitingroom.action';
import { LogAppointmentService } from '../../logging/providers/log-appointment.service';
import { AppointmentService } from '../../appointment/providers/appointment.service';
import { Appointment } from '../../appointment/store/one-time-appointment/appointment.entity';
import { TokenService } from '../../conference-v2/providers/token.service';
import { ProfileSelectService } from '../../profile/providers/profile-select.service';
import { BrandingService } from '../../branding/providers/branding.service';
import { BrandingState } from '../../branding/store/branding.state';
import { Institution } from '../../../entities/institution/institution.model';
import { Callee, SingleConferenceToken } from '../../conference-v2/store/conference.model';
import { Profile } from '../../profile/store/profile.entity';
import { ProfileService } from '../../profile/providers/profile.service';
import { MultiUserAppointment } from '../../appointment/store/appointment/multi-user-appointment.entity';
import { MultiUserAppointmentService } from '../../appointment/providers/multi-user-appointment.service';

export interface InstitutionSettings {
    name: Institution | null;
    brandLogoPath: string | null;
}

export interface StartConferencePayload {
    conferenceId: number;
    token: string;
    peerProfileId: number;
    peerBrowserName: string;
    inst: InstitutionSettings;
}

@Injectable()
export class WaitingroomEffects {
    constructor(private actions$: Actions,
                private tokenService: TokenService,
                private appointmentService: AppointmentService,
                private multiUserAppointmentService: MultiUserAppointmentService,
                private profileSelectService: ProfileSelectService,
                private profileService: ProfileService,
                private tracer: LogAppointmentService,
                private brandingService: BrandingService) {
    }

     activateConference$: Observable<Action> = createEffect(() => this.actions$.pipe(
        filter((action) => action.type === WAITINGROOM_IS_ONLINE || action.type === WAITINGROOM_SWITCH_CONFERENCE),
        filter((action: WaitingroomAction) => !!action.payload),
        map((action: WaitingroomAction) => new ConferenceActivateAction(<Conference>action.payload))));


     logStartVideoConferenceByDoctor$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(WAITINGROOM_START_VIDEO_CONFERENCE),
        tap(() => this.tracer.logAppointmentStarted())), {dispatch: false});

    private getConferenceIdFromPatient(): Observable<number> {
        return this.appointmentService.getAllAppointmentsObserver().pipe(
            take(1),
            map((appointments: Appointment[]) => appointments[0].conferenceId)
        );
    }

    private getSingleTokenConferencePayload(conferenceId: number): Observable<SingleConferenceToken> {
        return this.appointmentService.getAppointmentByConferenceId(conferenceId).pipe(
            take(1),
            mergeMap((appointment: Appointment) => this.profileService.getProfilesList().pipe(
                map((profileMap: { [id: number]: Profile }) => {
                    const filtered: string[] = Object.keys(profileMap)
                        .filter(key => appointment.profileIds.includes(+key))
                        .filter(key => Number(key) !== this.profileSelectService.getCurrentProfileId());
                    return filtered.map(id => profileMap[id]);
                }),
            )),
            take(1),
            map((calleeProfile: Profile[]) => Object.keys(calleeProfile)
                .map((profileKey) => ({
                    id: calleeProfile[profileKey].id,
                    name: this.getNameDisplayedInOVS(calleeProfile[profileKey]),
                    email: calleeProfile[profileKey].email,
                }))),
            map((callees: Callee[]) => ({
                conferenceId,
                userName: this.getNameDisplayedInOVS(this.profileSelectService.getCurrentProfile()),
                callee: callees
            }))
        );
    }

    private getSingleTokenMultiUserConferencePayload(conferenceId: number): Observable<SingleConferenceToken> {
        return this.multiUserAppointmentService.getMultiUserAppointmentByConferenceId(conferenceId).pipe(
            take(1),
            mergeMap((appointment: MultiUserAppointment) => this.profileService.getProfilesList().pipe(
                map((profileMap: { [id: number]: Profile }) => {
                    const filtered: string[] = Object.keys(profileMap)
                        .filter(key => appointment.profileIds.includes(+key))
                        .filter(key => Number(key) !== this.profileSelectService.getCurrentProfileId());
                    return filtered.map(id => profileMap[id]);
                }),
            )),
            take(1),
            map((calleeProfile: Profile[]) => Object.keys(calleeProfile)
                .map((profileKey) => ({
                    id: calleeProfile[profileKey].id,
                    name: this.getNameDisplayedInOVS(calleeProfile[profileKey]),
                    email: calleeProfile[profileKey].email,
                }))),
            map((callees: Callee[]) => ({
                conferenceId,
                userName: this.getNameDisplayedInOVS(this.profileSelectService.getCurrentProfile()),
                callee: callees
            }))
        );
    }

    private createConferencePayload(payload: SingleConferenceToken): Observable<StartConferencePayload> {
        return this.appointmentService.getAppointmentByConferenceId(payload.conferenceId).pipe(
            take(1),
            mergeMap((appointment: Appointment) => this.tokenService.getSingleConferenceToken(payload).pipe(
                switchMap((token: string) => this.brandingService.getBrandingSettingsFromStore().pipe(
                    take(1),
                    map((brandingState: BrandingState) => {
                        const logoUrl = brandingState.ovsLogoUrl || brandingState.logoUrl;
                        return {
                            token,
                            conferenceId: payload.conferenceId,
                            peerProfileId: this.getPeerProfileId(appointment.profileIds),
                            peerBrowserName: '',
                            inst: {
                                name: this.profileSelectService.getCurrentProfile() ?
                                    this.profileSelectService.getCurrentProfile().institution : null,
                                brandLogoPath: logoUrl ?
                                    window.location.origin + logoUrl : window.location.origin + '/assets/img/general/patientus-by-jameda.png'
                            }
                        }
                    })
                )),
            ))
        );
    }

    private createMultiUserConferencePayload(payload: SingleConferenceToken): Observable<StartConferencePayload> {
        return this.multiUserAppointmentService.getMultiUserAppointmentByConferenceId(payload.conferenceId).pipe(
            take(1),
            mergeMap((appointment: MultiUserAppointment) => this.tokenService.getSingleConferenceToken(payload).pipe(
                switchMap((token: string) => this.brandingService.getBrandingSettingsFromStore().pipe(
                    take(1),
                    map((brandingState: BrandingState) => {
                        const logoUrl = brandingState.ovsLogoUrl || brandingState.logoUrl;
                        return {
                            token,
                            conferenceId: payload.conferenceId,
                            peerProfileId: this.getPeerProfileId(appointment.profileIds),
                            peerBrowserName: '',
                            inst: {
                                name: this.profileSelectService.getCurrentProfile() ?
                                    this.profileSelectService.getCurrentProfile().institution : null,
                                brandLogoPath: logoUrl ?
                                    window.location.origin + logoUrl : window.location.origin + '/assets/img/general/patientus-by-jameda.png'
                            }
                        }
                    })
                )),
            ))
        );
    }

    private getPeerProfileId(profileIds: number[]): number {
        const currentProfileId = this.profileSelectService.getCurrentProfileId();
        return profileIds
            .filter((id: number) => currentProfileId !== id)
            .reduce((previousValue, currentValue) => currentValue, 0);
    }

    private getNameDisplayedInOVS(profile: Profile): string {
        return profile.firstName + ' ' + profile.lastName;
    }
}
